import RelayClient from '@relay/client';
import React from 'react';
import { flushSync } from 'react-dom';
import { hydrateRoot } from 'react-dom/client';
import { registrations } from '../../.relay/registrations';

// framer-motion 6 stamps a client-only data-projection-id attribute on motion
// elements once its layout-projection tree is active (any mounted draggable
// carousel activates it), so every component hydrating afterwards logs a React
// hydration-mismatch error for an attribute that is harmless and invisible in
// production (prod React doesn't log mismatch details at all). There is no fix
// inside fm6 - the attribute goes away when framer-motion is upgraded past v6,
// at which point this filter should be deleted. Scoped to exactly that message,
// development builds only.
if (process.env.NODE_ENV === 'development') {
  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ');
    if (text.includes('hydrated') && text.includes('data-projection-id')) {
      return;
    }
    originalConsoleError(...args);
  };
}

const relay = new RelayClient(registrations, async (registration, props, container) => {
  const { default: Component } = await registration.import();

  // RelayClient stamps data-relay-component-id on the first child of every
  // display:contents component to track in-view observation. React 19 reports
  // the stamp as a server/client attribute mismatch on hydrate - strip it
  // first (the client has already read it by the time this handler runs).
  container.querySelectorAll('[data-relay-component-id]').forEach((el) => {
    el.removeAttribute('data-relay-component-id');
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let reactComponent: any = React.createElement(Component, props as any);

  // if development wrap the component in a React.StrictMode
  if (process.env.NODE_ENV === 'development') {
    reactComponent = React.createElement(React.StrictMode, undefined, reactComponent);
  }

  flushSync(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hydrateRoot(container, reactComponent as any);
  });
});

/**
 * Hydration is deferred until the Cookiebot dialog has been answered, matching the
 * legacy IntersectionHydrator behaviour. Unlike the legacy hydrator, a fallback
 * timer force-hydrates when Cookiebot never loads at all (blocked script, adblock),
 * so the site can't end up permanently inert.
 */
const COOKIEBOT_POLL_MS = 60;
const COOKIEBOT_FALLBACK_MS = 10000;

function cookieDialogClosed(): boolean {
  // Cookiebot has not been loaded yet
  if (!window.Cookiebot) {
    return false;
  }

  // Cookiebot has been blocked by the client; don't hold hydration hostage
  if (typeof window.Cookiebot === 'object' && typeof window.Cookiebot.dialog === 'undefined') {
    return true;
  }

  // If previously accepted, dialog is set to null until opened again
  return window.Cookiebot.dialog === null || window.Cookiebot.dialog?.visible === false;
}

let initialised = false;

function initialiseOnce(): void {
  if (initialised) {
    return;
  }

  initialised = true;
  relay.initialise();
}

if (cookieDialogClosed()) {
  initialiseOnce();
} else {
  const poll = window.setInterval(() => {
    if (cookieDialogClosed()) {
      window.clearInterval(poll);
      initialiseOnce();
    }
  }, COOKIEBOT_POLL_MS);

  window.setTimeout(() => {
    window.clearInterval(poll);
    initialiseOnce();
  }, COOKIEBOT_FALLBACK_MS);
}

function fixImages(): void {
  const img = document.querySelectorAll<HTMLImageElement>('img[data-cfsrc]');

  img.forEach((img) => {
    const cfSrc = img.getAttribute('data-cfsrc');
    if (cfSrc !== null) {
      img.src = cfSrc;
    }
    img.setAttribute('style', '');
  });
}

window.addEventListener('CookiebotOnAccept', fixImages);
window.addEventListener('CookiebotOnDecline', fixImages);
